import { type ReactNode, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { LogOut, User, Wallet } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import { useClickOutside } from '@/hooks/use-click-outside';

const ITEM_CLS =
  'flex w-full items-center gap-sm rounded-md px-sm py-1.5 text-xs text-text-secondary transition-colors hover:bg-surface-secondary hover:text-brand-red';

type ItemProps = { icon: ReactNode; label: string; onClick: () => void };

function MenuItem({ icon, label, onClick }: ItemProps): ReactNode {
  return (
    <button onClick={onClick} className={ITEM_CLS}>
      {icon}
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
}

type Deps = {
  wrap: (fn: () => void) => () => void;
  navigate: ReturnType<typeof useNavigate>;
  logout: () => void;
};

function buildItems(d: Deps): ItemProps[] {
  return [
    {
      icon: <User size={13} />,
      label: 'Perfil',
      onClick: d.wrap(() => d.navigate('/profile')),
    },
    {
      icon: <Wallet size={13} />,
      label: 'Cartera',
      onClick: d.wrap(() => d.navigate('/wallet')),
    },
    {
      icon: <LogOut size={13} />,
      label: 'Cerrar sesion',
      onClick: d.wrap(d.logout),
    },
  ];
}

function useMenuItems(onClose: () => void): ItemProps[] {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const wrap = (fn: () => void) => () => {
    fn();
    onClose();
  };
  return buildItems({ wrap, navigate, logout });
}

function MenuContent({ onClose }: { onClose: () => void }): ReactNode {
  const items = useMenuItems(onClose);
  return (
    <>
      {items.map((p) => (
        <MenuItem key={p.label} {...p} />
      ))}
    </>
  );
}

type Props = {
  anchor: DOMRect;
  onClose: () => void;
  triggerRef?: React.RefObject<HTMLElement | null>;
};

function menuStyle(anchor: DOMRect): React.CSSProperties {
  const menuW = 192;
  return {
    left: Math.min(anchor.left, window.innerWidth - menuW - 16),
    bottom: window.innerHeight - anchor.top + 4,
  };
}

export function UserMenu({ anchor, onClose, triggerRef }: Props): ReactNode {
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, onClose, triggerRef);
  return createPortal(
    <div
      ref={ref}
      style={menuStyle(anchor)}
      className="fixed z-50 w-48 rounded-lg border border-border-subtle bg-surface-elevated p-xs shadow-md"
    >
      <MenuContent onClose={onClose} />
    </div>,
    document.body
  );
}
